import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  onClose: () => void;
  onPick: (entity: string) => void;
}

const tiles: Array<{ entity: string; icon: string; label: string; hint: string; full?: boolean }> = [
  { entity: 'workers', icon: '👷', label: 'Workers', hint: 'Name, role, phone, email, postcode…' },
  { entity: 'projects', icon: '🏗', label: 'Projects', hint: 'ID, name, customer, dates, status…' },
  { entity: 'customers', icon: '🏢', label: 'Customers', hint: 'Name, type, sector, contact details…' },
  { entity: 'contacts', icon: '👤', label: 'Contacts', hint: 'Name, company, role, email, phone…' },
  { entity: 'training', icon: '🎓', label: 'Training / Certs', hint: 'Worker name, cert type, expiry…' },
  { entity: 'suppliers', icon: '🚛', label: 'Suppliers', hint: 'Name, category, compliance, contact…' },
];

export function ImportWizardModal({ onClose, onPick }: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <div className="modal-title">📂 Import Data</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>Choose what you want to import — then use the full import wizard</div>
          </div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
          {tiles.map((t) => (
            <button
              key={t.entity}
              className="btn btn-ghost"
              onClick={() => { onClose(); onPick(t.entity); }}
              style={{ flexDirection: 'column', alignItems: 'center', padding: '16px 10px', height: 'auto', gap: 5, gridColumn: t.full ? '1 / -1' : undefined }}
            >
              <span style={{ fontSize: 22 }}>{t.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 12 }}>{t.label}</span>
              <span style={{ fontSize: 9, color: 'var(--muted)', textAlign: 'center' }}>{t.hint}</span>
            </button>
          ))}
        </div>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 11 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>How it works</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.8 }}>
            1 · Choose a data type above — the full import wizard opens<br />
            2 · Drop your CSV or Excel file — any column layout is fine<br />
            3 · Map your columns to CLS fields (auto-matched where possible)<br />
            4 · Review: new records, duplicates and conflicts shown before anything is saved<br />
            5 · Choose Skip, Overwrite or Merge for duplicates — then confirm
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
